/**
 * BackgroundTab: 설정 다이얼로그 > 배경 탭
 * v1 원본: RenewalComponents/Footer/SettingsDialog/BackgroundTab
 * Redux connect → useAppSelector/useAppDispatch 전환
 */
import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useAppSelector, useAppDispatch } from '@/store/hooks';
import { virtualBackgroundActions, type VirtualBackgroundType } from '@/store/slices/virtualBackgroundSlice';
import { createVirtualBackgroundEffect } from '@/utils/createVirtualBackgroundEffect';
import { useVirtualBackgroundImages } from '@/hooks/useVirtualBackgroundImages';
import type { JitsiStreamBackgroundEffect } from '@/utils/JitsiStreamBackgroundEffect';

// 프리셋 배경 이미지 목록
const BACKGROUND_IMAGES = [
  { id: 1, name: '은은한 배경', src: '/images/vbg/background-1.jpg' },
  { id: 2, name: '카페', src: '/images/vbg/background-2.jpg' },
  { id: 3, name: '오피스', src: '/images/vbg/background-3.jpg' },
  { id: 4, name: '노을', src: '/images/vbg/background-4.jpg' },
  { id: 5, name: '해변', src: '/images/vbg/background-5.jpg' },
  { id: 6, name: '도시', src: '/images/vbg/background-6.jpg' },
  { id: 7, name: '기하학', src: '/images/vbg/background-7.jpg' },
  { id: 8, name: '오로라', src: '/images/vbg/background-8.jpg' },
  { id: 9, name: '모던', src: '/images/vbg/background-9.jpg' },
];

const BackgroundTab: React.FC = () => {
  const dispatch = useAppDispatch();
  const virtualBackground = useAppSelector((s) => s.virtualBackground);
  const isVideoReverse = useAppSelector((s) => s.local.isVideoReverse);

  const { userImages, userImagesLoaded, isGuest, loadUserImages, uploadImage, deleteImage } = useVirtualBackgroundImages();

  const [hoverImageId, setHoverImageId] = useState<string | null>(null);

  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const uploadInputRef = useRef<HTMLInputElement>(null);
  const effectRef = useRef<JitsiStreamBackgroundEffect | null>(null);

  const stopEffect = useCallback(() => {
    effectRef.current?.stopEffect();
    effectRef.current = null;
  }, []);

  const applyPreview = useCallback((
    type: VirtualBackgroundType,
    imageUrl?: string | null
  ) => {
    const rawStream = streamRef.current;
    if (!rawStream || !videoRef.current) return;
    stopEffect();

    if (type === 'none') {
      videoRef.current.srcObject = rawStream;
      return;
    }

    const bgType = (type === 'blur' || type === 'slight_blur') ? 'blur' : 'image';
    const blurVal = type === 'blur' ? 80 : type === 'slight_blur' ? 30 : undefined;
    
    if (type === 'image' && !imageUrl) {
      videoRef.current.srcObject = rawStream;
      return;
    }

    const videoEl = videoRef.current;
    createVirtualBackgroundEffect({
      backgroundType: bgType,
      virtualSource: bgType === 'image' && imageUrl ? imageUrl : undefined,
      blurValue: blurVal,
    }).then((effect) => {
      if (!effect || !streamRef.current || !videoEl) return;
      effectRef.current = effect;
      videoEl.srcObject = effect.startEffect(streamRef.current);
    }).catch((err) => {
      console.warn('[VBG Effect] apply preview error', err);
      if (videoEl && streamRef.current) videoEl.srcObject = streamRef.current;
    });
  }, [stopEffect]);

  // 카메라 미리보기 시작
  useEffect(() => {
    const startCamera = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { width: 1280, height: 720 },
        });
        streamRef.current = stream;
        
        applyPreview(virtualBackground.type, virtualBackground.imageUrl);
        
        if (videoRef.current) {
          videoRef.current.onloadedmetadata = () => videoRef.current?.play();
        }
      } catch (err) {
        console.warn('[BackgroundTab] getUserMedia failed:', err);
      }
    };
    startCamera();

    // 서버에서 사용자 업로드 이미지 목록 로드
    if (!userImagesLoaded) loadUserImages();

    return () => {
      stopEffect();
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSelectBackground = useCallback(
    (type: VirtualBackgroundType, imageUrl?: string) => {
      applyPreview(type, imageUrl);

      dispatch(
        virtualBackgroundActions.setBackground({
          type,
          imageUrl: imageUrl || undefined,
        })
      );
    },
    [dispatch, applyPreview]
  );

  const handleAddClick = useCallback(() => {
    if (userImages.length >= 10) {
      return;
    }
    uploadInputRef.current?.click();
  }, [userImages.length]);

  const handleImageUpload = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      if (file.size > 5 * 1024 * 1024) {
        if (uploadInputRef.current) uploadInputRef.current.value = '';
        return;
      }

      const result = await uploadImage(file);
      if (result) {
        handleSelectBackground('image', result.src);
      }

      if (uploadInputRef.current) uploadInputRef.current.value = '';
    },
    [uploadImage, handleSelectBackground]
  );

  const handleDeleteImage = useCallback(
    (imgId: string, e: React.MouseEvent) => {
      e.stopPropagation();
      deleteImage(imgId);
      const target = userImages.find((img) => img.id === imgId);
      if (target && virtualBackground.imageUrl === target.src) {
        handleSelectBackground('none');
      }
    },
    [deleteImage, userImages, virtualBackground.imageUrl, handleSelectBackground]
  );

  const currentType = virtualBackground.type;

  return (
    <div className="common_customScrollbar">
      {/* 사용자 화면 (Preview) 영역 */}
      <div
        className="rtc14_screen rtc14_screen--bg rtc14_screen--inter4"
        style={{
          position: 'relative',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: '#000',
          borderRadius: '6px',
          overflow: 'hidden',
        }}
      >
        <span className="blind">사용자 설정 시뮬레이션 화면</span>
        <video
          ref={videoRef}
          autoPlay
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            transform: isVideoReverse ? '' : 'rotateY(180deg)',
            display: 'block',
          }}
        />
      </div>

      <div className="rtcDialogSetupNew__audio flexItem">
        <div className="rtcDialogSetupNew__audioItem">
          <div className="audioItem__title">
            <div className="audioItem__titleLeft">
              <strong>배경 선택</strong>
            </div>
            <div className="audioItem__titleOpt">
              {!isGuest && (
                <>
                  <button
                    type="button"
                    className="common_round_btn sizeSmall3 notosans_font"
                    onClick={handleAddClick}
                  >
                    이미지 추가
                  </button>
                  <input
                    ref={uploadInputRef}
                    style={{ display: 'none' }}
                    accept="image/*"
                    type="file"
                    onChange={handleImageUpload}
                  />
                </>
              )}
            </div>
          </div>

          <div className="audioItem__con" style={{ paddingBottom: '20px' }}>
            <div className="rtcNew_enterWrap__bgBox v2">
              <ul className="bgBoxList">
                {/* 없음 */}
                <li
                  className={currentType === 'none' ? 'selected' : ''}
                  onClick={() => handleSelectBackground('none')}
                >
                  <div className="bgBoxList__thum bgBoxList__thum--none">
                    <span className="thumText">없음</span>
                  </div>
                  <button type="button" className="bgBoxList__btn">
                    <span className="blind">선택</span>
                  </button>
                </li>

                {/* 흐리게 */}
                <li
                  className={currentType === 'blur' ? 'selected' : ''}
                  onClick={() => handleSelectBackground('blur')}
                >
                  <div className="bgBoxList__thum bgBoxList__thum--blur">
                    <div className="imgMask">
                      <img
                        src="https://static.wehago.com/imgs/rtc/dummy_rtc_bg_00.png"
                        className="bgBoxList__thumImg"
                        alt="배경 이미지"
                      />
                    </div>
                    <span className="thumText">흐리게</span>
                  </div>
                  <button type="button" className="bgBoxList__btn">
                    <span className="blind">선택</span>
                  </button>
                </li>
                
                {/* 프리셋 배경 이미지 */}
                {BACKGROUND_IMAGES.map((item) => (
                  <li
                    key={`preset_${item.id}`}
                    className={currentType === 'image' && virtualBackground.imageUrl === item.src ? 'selected' : ''}
                    onClick={() => handleSelectBackground('image', item.src)}
                  >
                    <div className="bgBoxList__thum">
                      <img
                        src={item.src}
                        className="bgBoxList__thumImg"
                        alt={item.name}
                      />
                    </div>
                    <button type="button" className="bgBoxList__btn">
                      <span className="blind">선택</span>
                    </button>
                  </li>
                ))}

                {/* 사용자 추가 이미지 목록 */}
                {[...userImages].reverse().map((item) => (
                  <li
                    key={`user_${item.id}`}
                    className={currentType === 'image' && virtualBackground.imageUrl === item.src ? 'selected' : ''}
                    onMouseEnter={() => setHoverImageId(item.id)}
                    onMouseLeave={() => setHoverImageId(null)}
                    onClick={() => handleSelectBackground('image', item.src)}
                  >
                    <div className="bgBoxList__thum">
                      <img src={item.src} className="bgBoxList__thumImg" alt="사용자 지정 이미지" />
                    </div>
                    <button type="button" className="bgBoxList__btn">
                      <span className="blind">선택</span>
                    </button>
                    {hoverImageId === item.id && (
                      <button
                        type="button"
                        className="bgBoxList__delBtn"
                        onClick={(e) => handleDeleteImage(item.id, e)}
                      >
                        <span className="blind">삭제</span>
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BackgroundTab;
